/**
 * Error handling utility functions for ServiceResult objects
 * Provides utilities for handling the new service result pattern
 */
import { ServiceResult } from '../../types/common/api.types';
import type { ProcessedError } from './errorHandler';

/**
 * Handles a service result and returns the data if successful, or null if failed
 * @param result - The ServiceResult from an API service
 * @param onError - Optional callback for error handling
 * @returns The data if successful, null if failed
 */
export function handleServiceResult<T>(
  result: ServiceResult<T>,
  onError?: (error: {
    message: string;
    details?: Record<string, string>;
  }) => void
): T | null {
  if (result.success) {
    return result.data!;
  } else {
    if (onError) {
      // Defensive: ServiceResult types error and message as optional. In practice all failed results
      // come from baseApi.createErrorResult(), which always sets both. We normalize anyway so (1) the
      // type checker is satisfied without assertions, and (2) any future code that returns a minimal
      // { success: false } still gives onError a valid { message, details? }.
      const err = result.error ?? {
        message: result.message ?? 'Service call failed',
        details: result.details,
      };
      onError({
        message: err.message,
        details: err.details,
      });
    }
    return null;
  }
}

/**
 * Handles a service result and throws an error if failed
 * @param result - The ServiceResult from an API service
 * @returns The data if successful
 * @throws Error if the service call failed
 */
export function unwrapServiceResult<T>(result: ServiceResult<T>): T {
  if (result.success) {
    return result.data!;
  } else {
    throw new Error(result.message || 'Service call failed');
  }
}

/**
 * Awaits a ServiceResult promise and returns data or throws.
 * For use with TanStack Query: queryFn/mutationFn must throw on failure so React Query can set
 * error state, trigger retries, and avoid caching failed results. Our API returns ServiceResult<T>;
 * this adapts it to the throw-on-error contract that TanStack Query expects.
 * When the result includes processedError (from baseApi), throws that so callers get correct errorType/details.
 */
export async function unwrapServiceResultAsync<T>(
  promise: Promise<ServiceResult<T>>
): Promise<T> {
  const result = await promise;
  if (result.success) return result.data!;
  const withProcessed = result as ServiceResult<T> & {
    processedError?: ProcessedError;
  };
  if (withProcessed.processedError) throw withProcessed.processedError;
  throw new Error(result.error?.message ?? result.message ?? 'Request failed');
}

/**
 * Checks if a service result is successful
 * @param result - The ServiceResult to check
 * @returns true if successful, false if failed
 */
export function isServiceSuccess<T>(
  result: ServiceResult<T>
): result is ServiceResult<T> & { success: true; data: T } {
  return result.success === true;
}

/**
 * Checks if a service result is an error
 * @param result - The ServiceResult to check
 * @returns true if failed, false if successful
 */
export function isServiceError<T>(
  result: ServiceResult<T>
): result is ServiceResult<T> & {
  success: false;
  error: { message: string; details?: Record<string, string> };
} {
  return result.success === false;
}
